// Factor-matching helpers and the year-term formula.
//
// Each helper:
//   - looks up the relevant table in src/data/,
//   - returns a number factor (1 as the no-op default per CLAUDE.md §5),
//   - emits a message into the supplied MessageSink when lookup fails or
//     a manual-review condition is hit.
//
// Industry orientation, industry project type, and project-level factors
// are now implemented per rules/RULES_INDUSTRIAL.md §2 and §3; project-
// level (棚改 / 留用 / 城市更新 项目修正) is still out of scope.

import {
  AFFORDABLE_HOUSING_TABLE,
  BUILDING_TYPE_TABLE,
  COMMERCIAL_FLOOR_TABLE,
  INDUSTRY_ORIENTATION_FACTORS,
  INDUSTRY_PROJECT_TYPE_FACTORS,
  LAND_TERM_PARAMS,
  PROPERTY_CONDITION_TABLE,
  UNDERGROUND_SPACE_TABLE,
} from "../data/index.js";
import type {
  ApplicablePriceType,
  AffordableHousingType,
  BuildingType,
  CommercialFloor,
  IndustryProjectType,
  PropertyCondition,
  PropertyConditionGroup,
  UndergroundType,
} from "../types/enums.js";
import { DEFAULT_FACTOR } from "../types/factor-chain.js";
import type { BuildingItem } from "../types/building-item.js";
import type { MarketPriceSet } from "../types/market-price.js";
import type { Parcel } from "../types/parcel.js";
import type { MessageSink } from "./message-sink.js";

// ---------------------------------------------------------------------------
// Industry factors
// ---------------------------------------------------------------------------

/**
 * Project types where the industry-orientation and industry-project-type
 * factors do NOT apply (per RULES_INDUSTRIAL.md §2 规则 3 and §3 规则 2).
 */
const INDUSTRY_EXCLUDED_PROJECT_TYPES: ReadonlySet<Parcel["projectType"]> =
  new Set<Parcel["projectType"]>(["城市更新", "留用土地", "棚户区改造"]);

/**
 * Whether a given item is a 配套商业 supporting facility. Per
 * RULES_INDUSTRIAL.md §2 规则 1 / §3 规则 1, supporting commercial is
 * excluded from the industry corrections.
 */
function isSupportingCommercial(item: BuildingItem): boolean {
  return item.isSupportingFacility === true && item.supportingFacilityType === "配套商业";
}

/**
 * Industry-orientation factor (RULES_INDUSTRIAL.md §2).
 *
 *   isIndustryOriented && !excluded-project-type && !supporting-commercial
 *     → 0.5  (qualified industry: 战略性新兴产业 / 优势传统产业 / 生产性现代服务业)
 *
 *   otherwise
 *     → 1
 *
 * When `isIndustryOriented` is true but the parcel's project type is
 * one of the excluded types (城市更新 / 留用土地 / 棚户区改造), we emit
 * a warning — the user likely intends the correction, but the rules
 * disallow it.
 *
 * When the item IS qualified industry, we also emit an info-level
 * note reminding the user that the industry classification must be
 * backed by the relevant authority's certification documents
 * (RULES_INDUSTRIAL.md §2 规则 2).
 */
export function getIndustryOrientationFactor(
  item: BuildingItem,
  parcel: Parcel,
  sink: MessageSink,
  field: string = "isIndustryOriented",
): number {
  if (!item.isIndustryOriented) return DEFAULT_FACTOR;

  if (INDUSTRY_EXCLUDED_PROJECT_TYPES.has(parcel.projectType)) {
    sink.warning(
      field,
      `产业发展导向修正系数不适用于 ${parcel.projectType} 项目。`,
      "该类项目的产业修正系数按 1 处理。",
    );
    return DEFAULT_FACTOR;
  }

  if (isSupportingCommercial(item)) {
    // Per rules, supporting commercial is excluded from the
    // industry-orientation correction. No warning — this is normal.
    return DEFAULT_FACTOR;
  }

  sink.info(
    field,
    "产业发展导向修正系数按 0.5 计收。",
    "产业分类与认定以相关部门证明文件为准，请人工确认。",
  );
  return INDUSTRY_ORIENTATION_FACTORS.qualified;
}

/**
 * Industry-project-type factor (RULES_INDUSTRIAL.md §3).
 *
 *   重点产业项目-联合申请       → 0.6
 *   重点产业项目-单一意向单位   → 0.7
 *   总部项目                    → 0.7
 *   一般产业项目                → 1
 *   城市更新 / 留用土地 / 棚改  → 1  (rules disallow; warn if flagged)
 *   配套商业                    → 1
 *   未设置 industryProjectType  → 1
 */
export function getIndustryProjectTypeFactor(
  item: BuildingItem,
  parcel: Parcel,
  sink: MessageSink,
  field: string = "industryProjectType",
): number {
  if (INDUSTRY_EXCLUDED_PROJECT_TYPES.has(parcel.projectType)) {
    if (item.industryProjectType !== undefined) {
      sink.warning(
        field,
        `产业项目类型修正系数不适用于 ${parcel.projectType} 项目。`,
        "该类项目的产业项目类型修正系数按 1 处理。",
      );
    }
    return DEFAULT_FACTOR;
  }

  if (isSupportingCommercial(item)) {
    return DEFAULT_FACTOR;
  }

  if (item.industryProjectType === undefined) {
    return DEFAULT_FACTOR;
  }

  const factor = INDUSTRY_PROJECT_TYPE_FACTORS[
    item.industryProjectType as IndustryProjectType
  ];
  if (factor === undefined) {
    sink.warning(
      field,
      `产业项目类型 ${item.industryProjectType} 不在表中，按 1 处理。`,
    );
    return DEFAULT_FACTOR;
  }
  return factor;
}

/**
 * Year-term correction factor per rules §4:
 *
 *   K = [1 - 1/(1+r)^m] / [1 - 1/(1+r)^n]
 *
 * - `remainingYears` < 1 is rounded up to 1 (per rules §4 rule 3).
 * - `m > n` is allowed by the math but triggers a manual-review warning.
 */
export function calculateLandTermFactor(
  priceType: ApplicablePriceType,
  remainingYears: number,
  sink: MessageSink,
  field: string = "landTermYears",
): number {
  const m = Math.max(Math.ceil(remainingYears), 1);
  const params = LAND_TERM_PARAMS[priceType];
  const r = params.r;
  const n = params.n;

  if (m > n) {
    sink.warning(
      field,
      `剩余年期 ${m} 超过标准使用年限 ${n}，请人工复核。`,
      "确认年期录入是否正确。",
    );
  }

  const numerator = 1 - 1 / Math.pow(1 + r, m);
  const denominator = 1 - 1 / Math.pow(1 + r, n);
  if (denominator === 0) {
    // Only happens if r=0; our tables never have r=0, but guard anyway.
    return DEFAULT_FACTOR;
  }
  return numerator / denominator;
}

/**
 * Returns the applicable market-price category for a building type, or
 * undefined if the type is not in the MVP table.
 */
export function getApplicablePriceType(
  buildingType: BuildingType,
  sink: MessageSink,
  field: string = "buildingType",
): ApplicablePriceType | undefined {
  const row = BUILDING_TYPE_TABLE[buildingType];
  if (!row) {
    sink.warning(field, `建筑类型 ${buildingType} 未在 MVP 系数表中找到，请人工确认。`);
    return undefined;
  }
  return row.applicablePriceType;
}

/**
 * Returns the building-type factor. Defaults to 1 if the building type
 * is not in the table (a warning is also emitted).
 */
export function getBuildingTypeFactor(
  buildingType: BuildingType,
  sink: MessageSink,
  field: string = "buildingType",
): number {
  const row = BUILDING_TYPE_TABLE[buildingType];
  if (!row) {
    sink.warning(field, `建筑类型 ${buildingType} 未在 MVP 系数表中找到，请人工确认。`);
    return DEFAULT_FACTOR;
  }
  return row.buildingTypeFactor;
}

/**
 * Returns the property-condition factor. Per rules §3 rule 1 and
 * CALCULATION_MVP.md §7 rule 1, affordable housing does NOT use the
 * property-condition factor.
 */
export function getPropertyConditionFactor(
  buildingType: BuildingType,
  propertyCondition: PropertyCondition | undefined,
  isAffordableHousing: boolean,
  sink: MessageSink,
  field: string = "propertyCondition",
): number {
  if (isAffordableHousing) {
    return DEFAULT_FACTOR;
  }
  if (!propertyCondition) {
    sink.error(field, "请选择产权条件。", "选择产权条件以匹配修正系数。");
    return DEFAULT_FACTOR;
  }
  const row = BUILDING_TYPE_TABLE[buildingType];
  if (!row) {
    sink.warning(
      field,
      `建筑类型 ${buildingType} 未识别产权分组，使用默认系数 1。`,
    );
    return DEFAULT_FACTOR;
  }
  const group: PropertyConditionGroup = row.propertyGroup;
  const groupTable = PROPERTY_CONDITION_TABLE[group];
  if (!groupTable) {
    sink.warning(field, `产权分组 ${group} 不在表中，请人工确认。`);
    return DEFAULT_FACTOR;
  }
  const factor = groupTable[propertyCondition];
  if (factor === undefined) {
    sink.error(
      field,
      `产权条件 ${propertyCondition} 在分组 ${group} 中未找到。`,
    );
    return DEFAULT_FACTOR;
  }
  return factor;
}

/**
 * Returns the affordable-housing factor. When the item is not
 * affordable housing, returns 1. When the type is "配售型保障性住房",
 * the rules say it must not be auto-calculated — we return 1 and emit
 * an error so the user is forced to handle it manually.
 */
export function getAffordableHousingFactor(
  isAffordableHousing: boolean | undefined,
  housingType: AffordableHousingType | undefined,
  sink: MessageSink,
  field: string = "affordableHousingType",
): number {
  if (!isAffordableHousing) return DEFAULT_FACTOR;
  if (!housingType) {
    sink.error(field, "已勾选保障性住房，请选择保障性住房类型。");
    return DEFAULT_FACTOR;
  }
  const entry = AFFORDABLE_HOUSING_TABLE[housingType];
  if (!entry) {
    sink.error(field, `保障性住房类型 ${housingType} 不在表中。`);
    return DEFAULT_FACTOR;
  }
  if (entry.requiresManualReview) {
    sink.error(
      field,
      `${housingType} 按有关规定执行，MVP 不自动测算。`,
      "请人工确认该分项地价。",
    );
    return DEFAULT_FACTOR;
  }
  return entry.factor ?? DEFAULT_FACTOR;
}

/**
 * Returns the above-ground commercial floor factor. Applies only to
 * above-ground 商业 — for 地下商业 see `getUndergroundSpaceFactor`.
 */
export function getCommercialFloorFactor(
  buildingType: BuildingType,
  isUnderground: boolean,
  commercialFloor: CommercialFloor | undefined,
  sink: MessageSink,
  field: string = "commercialFloor",
): number {
  if (buildingType !== "商业") return DEFAULT_FACTOR;
  if (isUnderground) return DEFAULT_FACTOR; // 地下商业 already handled by underground factor.
  if (!commercialFloor) {
    sink.error(
      field,
      "地上商业必须选择商业楼层。",
      "请在商业楼层下拉中选择首层 / 二层 / 三层 / 四层及以上。",
    );
    return DEFAULT_FACTOR;
  }
  const factor = COMMERCIAL_FLOOR_TABLE[commercialFloor];
  if (factor === undefined) {
    sink.error(field, `商业楼层 ${commercialFloor} 不在表中。`);
    return DEFAULT_FACTOR;
  }
  return factor;
}

/**
 * Returns the underground space factor. Applies only when the item is
 * underground. Per rules §7, 地下商业 uses the above-ground 商业 price
 * (handled by getApplicablePriceType / market-price lookup) and then
 * applies this factor — the commercial-floor factor is intentionally
 * not applied on top.
 */
export function getUndergroundSpaceFactor(
  isUnderground: boolean,
  undergroundType: UndergroundType | undefined,
  sink: MessageSink,
  field: string = "undergroundType",
): number {
  if (!isUnderground) return DEFAULT_FACTOR;
  if (!undergroundType) {
    sink.error(
      field,
      "地下空间必须选择地下空间类型。",
      "请在地下空间类型下拉中选择交通设施或综合管廊 / 其他地下空间。",
    );
    return DEFAULT_FACTOR;
  }
  const factor = UNDERGROUND_SPACE_TABLE[undergroundType];
  if (factor === undefined) {
    sink.error(field, `地下空间类型 ${undergroundType} 不在表中。`);
    return DEFAULT_FACTOR;
  }
  return factor;
}

/**
 * Resolves a market price for a given applicable price type. Returns
 * undefined and emits an error when the price is missing or non-positive.
 * Per CLAUDE.md §4 rule 8, prices must come from the user / external
 * data — the calculator must never substitute one price for another.
 */
export function getMarketPrice(
  priceType: ApplicablePriceType,
  prices: MarketPriceSet,
  sink: MessageSink,
  field: string = "marketPrice",
): number | undefined {
  let raw: number | undefined;
  switch (priceType) {
    case "住宅":
      raw = prices.residential;
      break;
    case "商业":
      raw = prices.commercial;
      break;
    case "办公":
      raw = prices.office;
      break;
    case "工业":
      raw = prices.industrial;
      break;
    case "新型产业":
      raw = prices.m0; // m0 maps to the新型产业 market price in the spec.
      break;
    case "酒店":
      raw = prices.hotel;
      break;
    case "公共管理与服务设施":
      raw = prices.publicService;
      break;
    case "公用设施":
      raw = prices.utility;
      break;
  }
  if (raw === undefined || raw === null || Number.isNaN(raw) || raw <= 0) {
    sink.error(
      field,
      `缺少适用地价类型 ${priceType} 的市场价格。`,
      "请录入或导入对应市场价格。",
    );
    return undefined;
  }
  return raw;
}

/**
 * Office-price floor for above-ground commercial.
 *
 * Returns the (possibly adjusted) market price and a note. Per
 * CALCULATION_MVP.md §10 we use approach #2: when triggered, the
 * adjusted market price is set to the office price and the caller's
 * commercial-floor factor remains 1 (so the price-floor note explains
 * what happened).
 */
export function applyCommercialOfficePriceFloor(
  buildingType: BuildingType,
  isUnderground: boolean,
  commercialFloor: CommercialFloor | undefined,
  commercialFloorFactor: number,
  commercialPrice: number | undefined,
  officePrice: number | undefined,
  sink: MessageSink,
): { marketPrice: number; commercialFloorFactor: number; note?: string } {
  if (buildingType !== "商业" || isUnderground) {
    return {
      marketPrice: commercialPrice ?? 0,
      commercialFloorFactor,
    };
  }
  if (commercialPrice === undefined) {
    return { marketPrice: 0, commercialFloorFactor };
  }
  if (officePrice === undefined) {
    sink.warning(
      "marketPrice",
      "未录入办公市场价格，无法校验商业楼层办公价格兜底规则。",
    );
    return { marketPrice: commercialPrice, commercialFloorFactor };
  }
  const adjusted = commercialPrice * commercialFloorFactor;
  if (adjusted < officePrice) {
    return {
      marketPrice: officePrice,
      commercialFloorFactor: 1,
      note:
        "商业楼层修正后价格低于办公用途价格，已按办公用途价格兜底。",
    };
  }
  return { marketPrice: commercialPrice, commercialFloorFactor };
}
